/**
 * MANAGEMENT (15) — ANALYTICS presentation logic. PURE (no React, no server): the DZD money formatters,
 * the token color ramps + categorical palette, and the chart-data shaping the flagship Analytics section
 * consumes. Token colors are returned as `var(--nv-*)` (no raw hex, L-2); the FR formatters keep figures
 * grouped fr-FR (they read LTR via `.nv-ltr-island` upstream). Mirrors the sibling metrics.lib posture.
 *
 * MONEY — every amount the server sends is integer centimes DZD. `fmtDzdCentimes` is the single money
 * formatter the section uses (centimes → a grouped DZD string with the unit); `fmtDzdCompact` gives the
 * KPI-tile short form (k/M DA) so a 12-figure MRR doesn't overflow a tile.
 */
import { token } from "@/design/theme";
import { fmtCount } from "./metrics.lib";
import type { ChartDatum } from "./metrics.lib";
import type { AnalyticsBucket, AnalyticsPeriod } from "./analytics.types";

export type { ChartDatum } from "./metrics.lib";
export { fmtCount, fmtPct, shortBucket } from "./metrics.lib";

// ── Money (integer centimes DZD → FR-grouped strings) ─────────────────────────────────

const nfDzd = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 });
const nfDzd2 = new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nfPct1 = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 });

/** Integer centimes → a grouped whole-DZD string with the unit (e.g. `1 250 000 DA`). */
export function fmtDzdCentimes(centimes: number): string {
  return `${nfDzd.format(Math.round(centimes / 100))} DA`;
}

/** Integer centimes → a 2-decimal DZD string (used where the exact dinar matters, e.g. a tooltip). */
export function fmtDzdCentimesExact(centimes: number): string {
  return `${nfDzd2.format(centimes / 100)} DA`;
}

/** Integer centimes → a COMPACT DZD string for a KPI tile (`1,3 M DA` / `250 k DA` / `900 DA`). */
export function fmtDzdCompact(centimes: number): string {
  const dzd = centimes / 100;
  const abs = Math.abs(dzd);
  if (abs >= 1_000_000) return `${nfPct1.format(dzd / 1_000_000)} M DA`;
  if (abs >= 1_000) return `${nfPct1.format(dzd / 1_000)} k DA`;
  return `${nfDzd.format(dzd)} DA`;
}

/** A percentage figure (already a 0..100 number) → FR-grouped with one decimal + the unit. */
export function fmtPct1(value: number): string {
  return `${nfPct1.format(value)} %`;
}

// ── Palettes (token-driven — no raw hex, daltonisme-safe ordering) ──────────────────────

/**
 * The categorical ramp the ranking bars / multi-series charts cycle through. Ordered azure → success →
 * warn → info → hold → steel → navy so adjacent slices stay distinguishable; repeats gracefully past 7.
 * All `var(--nv-*)` (L-2).
 */
export const ANALYTICS_PALETTE: ReadonlyArray<string> = [
  token.azure,
  token.success,
  token.warn,
  token.info,
  token.hold,
  token.steel,
  token.navy,
];

/** Deterministic color for the i-th series/slice (wraps the palette). */
export function paletteColor(i: number): string {
  return ANALYTICS_PALETTE[i % ANALYTICS_PALETTE.length]!;
}

/** Free-vs-paid referral coloring (free = neutral steel, paid = success — money is good). */
export const FREE_PAID_COLOR = Object.freeze({ free: token.steel, paid: token.success });

/** Demand-band coloring — a cool→warm ramp (none = steel, low = azure, medium = warn, high = success). */
export const DEMAND_BAND_COLOR: Readonly<Record<string, string>> = Object.freeze({
  none: token.steel,
  low: token.azure,
  medium: token.warn,
  high: token.success,
});

// ── Chart data shaping ─────────────────────────────────────────────────────────────────

/** One point on a merged multi-series time chart (a `date` + one numeric key per series). */
export interface MergedSeriesPoint {
  date: string;
  [seriesKey: string]: number | string;
}

/**
 * Shape a `[label, value]` list into ranked bar data, colored from the categorical palette. Drops nothing
 * (a zero bar reads as "present, empty"); the caller decides the empty state from the total.
 */
export function toRankedBars(
  entries: ReadonlyArray<{ key: string; label: string; value: number }>,
): ChartDatum[] {
  return entries.map((e, i) => ({ key: e.key, name: e.label, value: e.value, color: paletteColor(i) }));
}

/** Shape an explicit `{ key,label,value,color }` list into donut data, dropping zero slices. */
export function toColoredDonut(
  entries: ReadonlyArray<{ key: string; label: string; value: number; color: string }>,
): ChartDatum[] {
  return entries
    .filter((e) => e.value > 0)
    .map((e) => ({ key: e.key, name: e.label, value: e.value, color: e.color }));
}

/** True when every value in a numeric list is 0 (drives a chart's empty state). */
export function allZero(values: ReadonlyArray<number>): boolean {
  return values.every((v) => v === 0);
}

/** Sum a numeric list (the ranking-total / empty-check helper). */
export function sum(values: ReadonlyArray<number>): number {
  return values.reduce((s, v) => s + v, 0);
}

/**
 * Fold a set of per-category trend series (each `{ categorie, points:[{date,value}] }`) onto ONE merged
 * axis keyed by date, with one numeric field per category — the shape a stacked/multi-line Recharts chart
 * consumes. Assumes every series shares the same ascending `points` axis (the server guarantees it).
 */
export function mergeTrendSeries(
  buckets: ReadonlyArray<string>,
  series: ReadonlyArray<{ categorie: string; points: ReadonlyArray<{ date: string; value: number }> }>,
): MergedSeriesPoint[] {
  const byDate = new Map<string, MergedSeriesPoint>();
  for (const b of buckets) byDate.set(b, { date: b });
  for (const s of series) {
    for (const p of s.points) {
      const row = byDate.get(p.date);
      if (row) row[s.categorie] = p.value;
    }
  }
  // Backfill any missing series field with 0 so Recharts never plots `undefined`.
  const cats = series.map((s) => s.categorie);
  const rows = [...byDate.values()];
  for (const row of rows) for (const c of cats) if (row[c] == null) row[c] = 0;
  return rows;
}

/** FR label for a period token (used in a Card subtitle / aria summary). */
export function periodLabelKey(period: AnalyticsPeriod): string {
  return `mgmt.overview.period.${period}`;
}

/** Re-export so the section can build a settled-cash trend title without re-deriving. */
export function totalOfCounts(entries: ReadonlyArray<{ count: number }>): number {
  return entries.reduce((s, e) => s + e.count, 0);
}

/** Convenience: a short human string for a ranked count row (used in aria summaries). */
export function rankSummary(label: string, count: number): string {
  return `${label}: ${fmtCount(count)}`;
}

/** The bucket grain a period maps to (mirrors the server: 12mo → month, else day). */
export function bucketForPeriod(period: AnalyticsPeriod): AnalyticsBucket {
  return period === "12mo" ? "month" : "day";
}
